/**
 * AddServiceDialog — Ted #23 (#984) + Ted #20 Part B (#940).
 *
 * Ted, on Milo's checkout: *"When checking out Milo, I did not see the
 * additional services that had been added to his reservation … it appeared
 * that I was checking Milo out only for Doggy Daycare."* And, on the Facility
 * Calendar: *"if a client or staff member adds grooming or another add-on
 * after the dog has already been checked in, it may not appear anywhere
 * obvious. We have actually missed add-on services because of this."*
 *
 * Until now Animal Lovers had NO desk path at all to put a service on an
 * existing visit — not at booking, not after check-in. This is that path.
 * What it adds is a SERVICE LEG: a real booking hanging off the visit, so it
 * shows on the day list, the assignee's schedule and the checkout itemization
 * the moment it is saved.
 *
 * Design-Philosophy principles served:
 *  - #1 One glance — one question per row: what, when, who.
 *  - #2 Next action obvious — the primary button says what will happen
 *    ("Add to this visit"), and the note under it says where it will show up.
 *  - #5 Never lie — THE PRICE SHOWN IS THE PRICE THE SERVER WILL CHARGE.
 *    Not "the catalog price": the 09-16 review caught this dialog quoting
 *    `base_price_cents` while the server ran the tenant's pricing rules, so a
 *    $40 bath on a Saturday would have been read aloud as $40 and billed at
 *    $48. Every number here now comes from `GET /:id/service-options`, which
 *    prices each option with the SAME function the add path charges with, for
 *    THIS visit's dog count and date. There is no editable price and no price
 *    field on the request — a desk that could type a number into this box
 *    would be quoting one the server never agreed to.
 *  - #7 Built for hands — every control ≥44 px; the dialog is a single
 *    column at 375 px.
 *
 * Ted #29 (#1108), 2026-09-30, on Quin's boarding: *"I have to repeat that
 * entire process for every day — potentially 4, 10, or even 15 times for
 * one stay."* So on a multi-day stay the dialog asks one more thing — APPLY
 * TO: this date / every day of the stay / choose dates — and the "who is
 * doing it" picker is the PERFORMED-BY Ted asked to record. The total shown
 * before Apply is not N × the one-day price computed here: it is the
 * server's own preview of the SAME plan the Apply writes
 * (`POST /:id/legs/batch` with `preview: true`), priced per date by the one
 * function that charges, with dates already carrying the service reported
 * and skipped. Principle #5, applied to a sum rather than a line.
 *
 * The five component states are all here, and ERROR AND EMPTY ARE NOW
 * DISTINGUISHABLE AT THE DATA LAYER (09-16 review): idle (the form), loading
 * (the catalog fetch), busy (saving, controls disabled), error (the server's
 * own message — a failed catalog read says the list could not be loaded and
 * offers Try again, instead of the old behaviour of degrading to `[]` and
 * telling the desk to go configure Settings), and empty (a tenant that really
 * has no non-floor services says so).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addServiceLeg,
  adminStaffApi,
  applyServiceAcrossStay,
  fetchServiceLegOptions,
  TRANSPORT_MODE_LABELS,
  type AdminServiceLegOption,
  type AdminStaffUser,
  type AdminStayServicePlanLine,
  type TransportMode,
} from '../../lib/api.js';
import { pickableStaff } from '../../lib/staff-picker.js';
import { formatServiceDay } from '../../lib/outstanding.js';
import './AddServiceDialog.css';

/** Ted #29 — how many of the stay's days the service lands on. */
export type ApplyTo = 'this-date' | 'every-day' | 'choose-dates';

/*
 * The ON-FLOOR filter used to live here as a duplicated three-item name list.
 * It is gone: `GET /:id/service-options` applies `countsTowardFacilityHeadcount`
 * server-side, so the picker and the refusal (`leg_service_on_floor`) now read
 * the same list — there is no second copy to drift.
 */

export interface AddServiceDialogProps {
  readonly apiUrl: string;
  readonly token: string;
  /** The visit the service is being added to. */
  readonly bookingId: string;
  /** "Milo — Full-Day Daycare" — what the dialog is about, in the desk's words. */
  readonly bookingLabel: string;
  /** True when the dog is already here; changes the confirmation copy only. */
  readonly parentCheckedIn: boolean;
  readonly onClose: () => void;
  /** Called after a successful add so the caller can reload its list. */
  readonly onAdded: () => void;
}

type Choice = AdminServiceLegOption;

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Ted #28 Phase 1b (#1050) — the transport line item.
 *
 * Ted: Gingr has a $15 line and a $30 line "but the higher amount is really
 * dependent on the type of transportation … I'd love the ability to have the
 * service or line item in the system but still allow the price to be
 * overridden when necessary." So a TRANSPORT option asks two more things:
 * which way the van goes (it sets the visit's flag, and a ride in is the
 * hard stop at check-in), and — only if the desk opens it — a different
 * price WITH a reason. The override is the one exception to principle #5
 * above, and it is a #906-shaped adjustment, not a free price field: the
 * server refuses it on any other service, records the reason on the
 * ledger, and holds the desk to the same 20% band as Adjust price.
 */
type Direction = Exclude<TransportMode, 'none'>;

const DIRECTIONS: ReadonlyArray<{ value: Direction; label: string }> = [
  { value: 'arrival', label: 'Ride in — we pick the dog up' },
  { value: 'departure', label: 'Ride home — we drop the dog off' },
  { value: 'both', label: 'Both ways' },
];

/** "30" / "30.50" / "$30" → cents; anything else → null. Exported for the tests. */
export function dollarsToCents(raw: string): number | null {
  const cleaned = raw.trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function AddServiceDialog({
  apiUrl,
  token,
  bookingId,
  bookingLabel,
  parentCheckedIn,
  onClose,
  onAdded,
}: AddServiceDialogProps): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [dogCount, setDogCount] = useState(1);
  /**
   * The CATALOG read's own failure, kept apart from the save error below.
   * Before the 09-16 review both collapsed into "no services configured" —
   * the dialog told the desk to fix a configuration that was already correct.
   */
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [staff, setStaff] = useState<AdminStaffUser[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [assignee, setAssignee] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ted #28 Phase 1b — transport only. The visit's CURRENT flag comes from
  // the same options read, so the copy can say what this leg changes it to.
  const [stayTransport, setStayTransport] = useState<TransportMode>('none');
  const [direction, setDirection] = useState<Direction | ''>('');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  // Ted #29 (#1108) — the stay's days (one on a daycare visit), the desk's
  // "apply to" answer, and the server's priced preview of the plan.
  const [visitDate, setVisitDate] = useState<string>('');
  const [stayDates, setStayDates] = useState<string[]>([]);
  const [applyTo, setApplyTo] = useState<ApplyTo>('this-date');
  const [chosenDates, setChosenDates] = useState<ReadonlySet<string>>(new Set());
  const [quote, setQuote] = useState<{ plan: AdminStayServicePlanLine[]; totalCents: number } | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setCatalogError(null);
    void (async () => {
      try {
        // ONE priced answer for THIS visit — see fetchServiceLegOptions. It
        // throws rather than degrading to [], which is what makes the error
        // state reachable at all.
        const res = await fetchServiceLegOptions(apiUrl, token, bookingId);
        if (!live) return;
        setChoices(res.options);
        setDogCount(res.dogCount > 0 ? res.dogCount : 1);
        setStayTransport(res.transport);
        setVisitDate(res.date);
        setStayDates(res.stayDates);
      } catch (e) {
        if (!live) return;
        setChoices([]);
        setCatalogError(
          e instanceof Error ? e.message : 'Could not load the service list.',
        );
      } finally {
        if (live) setLoading(false);
      }
      // The staff picker is a CONVENIENCE, not a requirement: a receptionist
      // may not hold staff:read. A failure here leaves the leg unassigned,
      // which the day list handles on purpose — it lands in "Needs a
      // specialist" rather than nowhere. Never block the add on it.
      const staffRes = await adminStaffApi.getStaff(apiUrl).catch(() => null);
      // Ted Item 32.1 — a new leg has no one recorded yet, so only people
      // still on staff are offered (departed staff stay on the Staff page).
      if (live && staffRes?.data?.staff) setStaff(pickableStaff(staffRes.data.staff));
    })();
    return () => {
      live = false;
    };
  }, [apiUrl, token, bookingId, reloadKey]);

  const addOns = useMemo(() => choices.filter((c) => c.kind === 'addOn'), [choices]);
  const services = useMemo(() => choices.filter((c) => c.kind === 'service'), [choices]);

  const choice = choices.find((c) => `${c.kind}:${c.id}` === selected) ?? null;
  const isTransport = choice?.transport === true;

  // ── Ted #29 — the dates this add lands on ─────────────────────────────
  // A multi-day stay offers the choice; a one-day visit (or a transport
  // line, which has a direction and is added once) is always "this date".
  const isStay = stayDates.length > 1;
  const offersApplyTo = isStay && !isTransport;
  const effectiveApplyTo: ApplyTo = offersApplyTo ? applyTo : 'this-date';
  const batchDates = useMemo<string[]>(() => {
    if (effectiveApplyTo === 'every-day') return stayDates;
    if (effectiveApplyTo === 'choose-dates') return stayDates.filter((d) => chosenDates.has(d));
    return [];
  }, [effectiveApplyTo, stayDates, chosenDates]);
  const isBatch = effectiveApplyTo !== 'this-date';
  const batchBody = useMemo(
    () =>
      choice && isBatch
        ? {
            ...(choice.kind === 'addOn' ? { addOnId: choice.id } : { serviceTypeId: choice.id }),
            ...(effectiveApplyTo === 'every-day' ? { everyDay: true } : { dates: batchDates }),
            ...(startTime ? { startTime } : {}),
            ...(assignee ? { assignedStaffId: assignee } : {}),
          }
        : null,
    [choice, isBatch, effectiveApplyTo, batchDates, startTime, assignee],
  );

  // THE QUOTE IS THE SERVER'S. The same request as Apply, with `preview`,
  // so the sum on screen is the sum of the lines the server will write —
  // per-date pricing rules and already-present dates included. Debounced:
  // ticking five dates is five changes, one request.
  useEffect(() => {
    if (!batchBody || batchDates.length === 0) {
      setQuote(null);
      setQuoteError(null);
      setQuoting(false);
      return;
    }
    let live = true;
    setQuoting(true);
    setQuoteError(null);
    const t = window.setTimeout(() => {
      void applyServiceAcrossStay(apiUrl, token, bookingId, { ...batchBody, preview: true })
        .then((res) => {
          if (!live) return;
          setQuote({ plan: res.plan, totalCents: res.totalCents });
        })
        .catch((e: unknown) => {
          if (!live) return;
          setQuote(null);
          setQuoteError(e instanceof Error ? e.message : 'Could not price these dates.');
        })
        .finally(() => {
          if (live) setQuoting(false);
        });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [apiUrl, token, bookingId, batchBody, batchDates.length]);

  const toggleDate = useCallback((date: string) => {
    setChosenDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }, []);

  // The override is only ever SENT for a transport service with the panel
  // open, a parseable amount and a reason — otherwise the body carries no
  // price at all, exactly as before this change.
  const overrideCents = overrideOpen && isTransport ? dollarsToCents(overrideAmount) : null;
  const overrideReasonOk = overrideReason.trim().length >= 2;
  const overrideReady = !overrideOpen || !isTransport || (overrideCents !== null && overrideReasonOk);
  const directionReady = !isTransport || direction !== '';
  // A batch needs at least one date, and a quote that agrees with it — the
  // button never promises a number the server has not said.
  const newLines = quote ? quote.plan.filter((l) => l.existingLegId === null).length : 0;
  const batchReady = !isBatch || (batchDates.length > 0 && quote !== null && !quoting && newLines > 0);
  const canSubmit = Boolean(choice) && directionReady && overrideReady && batchReady && !busy;

  const submit = useCallback(async () => {
    if (!choice || !canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      if (batchBody) {
        // Ted #29 — one call, one transaction, one leg per date. The same
        // body the preview priced, minus `preview`.
        await applyServiceAcrossStay(apiUrl, token, bookingId, batchBody);
        onAdded();
        onClose();
        return;
      }
      await addServiceLeg(apiUrl, token, bookingId, {
        ...(choice.kind === 'addOn' ? { addOnId: choice.id } : { serviceTypeId: choice.id }),
        ...(startTime ? { startTime } : {}),
        ...(assignee ? { assignedStaffId: assignee } : {}),
        // Ted #28 Phase 1b — transport only, and only what the desk chose.
        ...(isTransport && direction ? { transport: direction } : {}),
        ...(isTransport && overrideOpen && overrideCents !== null && overrideReasonOk
          ? { priceOverride: { totalCents: overrideCents, reason: overrideReason.trim() } }
          : {}),
      });
      onAdded();
      onClose();
    } catch (e) {
      // The server's message is the one that knows why (wrong status, a
      // service that would double-count the dog on the floor, a price past
      // the desk band, …).
      setError(e instanceof Error ? e.message : 'Could not add the service.');
      setBusy(false);
    }
  }, [
    apiUrl,
    token,
    bookingId,
    choice,
    canSubmit,
    batchBody,
    startTime,
    assignee,
    isTransport,
    direction,
    overrideOpen,
    overrideCents,
    overrideReasonOk,
    overrideReason,
    onAdded,
    onClose,
  ]);

  const stayFlagLabel = stayTransport === 'none' ? null : TRANSPORT_MODE_LABELS[stayTransport];
  const skippedLines = quote ? quote.plan.length - newLines : 0;
  const submitLabel = busy
    ? 'Adding…'
    : isBatch
      ? quote && newLines > 0
        ? `Apply to ${newLines} ${newLines === 1 ? 'day' : 'days'} — ${money(quote.totalCents)}`
        : 'Apply to the stay'
      : 'Add to this visit';

  return (
    <div className="ferro-add-service__overlay" data-testid="add-service-overlay">
      <div
        className="ferro-add-service__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-service-title"
        data-testid="add-service-dialog"
      >
        <h2 className="ferro-add-service__title" id="add-service-title">
          Add a service
        </h2>
        <p className="ferro-add-service__subject" data-testid="add-service-subject">
          {bookingLabel}
        </p>

        {loading ? (
          <p className="ferro-muted" data-testid="add-service-loading">
            Loading services&hellip;
          </p>
        ) : catalogError ? (
          /* ERROR ≠ EMPTY (09-16 review). A 403 or a dead server is not a
             configuration problem, and telling the desk to go edit Settings
             would send them to fix something that is already correct. */
          <div className="ferro-add-service__catalog-error" data-testid="add-service-catalog-error">
            <p role="alert">{catalogError}</p>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="add-service-retry"
            >
              Try again
            </button>
          </div>
        ) : choices.length === 0 ? (
          <p className="ferro-muted" data-testid="add-service-empty">
            There are no add-ons or non-facility services set up yet. Add them
            under Settings &rarr; Services first.
          </p>
        ) : (
          <>
            <label className="ferro-field">
              <span className="ferro-field__label">Service</span>
              <select
                id="add-service-picker"
                name="service"
                className="ferro-input"
                value={selected}
                disabled={busy}
                onChange={(e) => setSelected(e.target.value)}
                data-testid="add-service-picker"
              >
                <option value="">Pick a service&hellip;</option>
                {addOns.length > 0 ? (
                  <optgroup label="Add-ons">
                    {addOns.map((a) => (
                      <option key={a.id} value={`addOn:${a.id}`}>
                        {a.label} — {money(a.totalCents)}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
                {services.length > 0 ? (
                  <optgroup label="Services">
                    {services.map((sv) => (
                      <option key={sv.id} value={`service:${sv.id}`}>
                        {sv.label} — {money(sv.totalCents)}
                      </option>
                    ))}
                  </optgroup>
                ) : null}
              </select>
            </label>

            {/* Ted #29 (#1108) — APPLY TO. Only on a multi-day stay, and never
                for transport (a ride has a direction and is added once). */}
            {offersApplyTo ? (
              <fieldset className="ferro-add-service__apply" data-testid="add-service-apply-to">
                <legend className="ferro-field__label">Apply to</legend>
                <label className="ferro-field ferro-field--inline">
                  <input
                    type="radio"
                    id="add-service-apply-this-date"
                    name="add-service-apply-to"
                    value="this-date"
                    checked={applyTo === 'this-date'}
                    disabled={busy}
                    onChange={() => setApplyTo('this-date')}
                    data-testid="add-service-apply-this-date"
                  />
                  <span>Just {visitDate ? formatServiceDay(visitDate) : 'this date'}</span>
                </label>
                <label className="ferro-field ferro-field--inline">
                  <input
                    type="radio"
                    id="add-service-apply-every-day"
                    name="add-service-apply-to"
                    value="every-day"
                    checked={applyTo === 'every-day'}
                    disabled={busy}
                    onChange={() => setApplyTo('every-day')}
                    data-testid="add-service-apply-every-day"
                  />
                  <span>
                    Every day of the stay — {stayDates.length} days, {formatServiceDay(stayDates[0]!)} to{' '}
                    {formatServiceDay(stayDates[stayDates.length - 1]!)}
                  </span>
                </label>
                <label className="ferro-field ferro-field--inline">
                  <input
                    type="radio"
                    id="add-service-apply-choose-dates"
                    name="add-service-apply-to"
                    value="choose-dates"
                    checked={applyTo === 'choose-dates'}
                    disabled={busy}
                    onChange={() => setApplyTo('choose-dates')}
                    data-testid="add-service-apply-choose-dates"
                  />
                  <span>Choose dates</span>
                </label>
                {applyTo === 'choose-dates' ? (
                  <div className="ferro-add-service__dates" data-testid="add-service-dates">
                    {stayDates.map((d) => (
                      <label key={d} className="ferro-field ferro-field--inline ferro-add-service__date">
                        <input
                          type="checkbox"
                          id={`add-service-date-${d}`}
                          name="add-service-dates"
                          value={d}
                          checked={chosenDates.has(d)}
                          disabled={busy}
                          onChange={() => toggleDate(d)}
                          data-testid={`add-service-date-${d}`}
                        />
                        <span>{formatServiceDay(d)}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </fieldset>
            ) : null}

            <label className="ferro-field">
              <span className="ferro-field__label">Time (optional)</span>
              <input
                id="add-service-time"
                name="startTime"
                className="ferro-input"
                type="time"
                value={startTime}
                disabled={busy}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="add-service-time"
              />
              <span className="ferro-field__hint">
                Leave blank if it is just &ldquo;sometime that day&rdquo; — it
                still shows on the day list.
              </span>
            </label>

            {staff.length > 0 ? (
              <label className="ferro-field">
                {/* Ted #29 — "record who performed the service". Recorded on
                    every leg this add creates; editable per date afterwards. */}
                <span className="ferro-field__label">Performed by (optional)</span>
                <select
                  id="add-service-assignee"
                  name="assignedStaffId"
                  className="ferro-input"
                  value={assignee}
                  disabled={busy}
                  onChange={(e) => setAssignee(e.target.value)}
                  data-testid="add-service-assignee"
                >
                  <option value="">Nobody yet — needs a specialist</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {isTransport ? (
              <>
                <label className="ferro-field">
                  <span className="ferro-field__label">Which way does the van go?</span>
                  <select
                    id="add-service-transport"
                    name="transport"
                    className="ferro-input"
                    value={direction}
                    disabled={busy}
                    onChange={(e) => setDirection(e.target.value as Direction | '')}
                    data-testid="add-service-transport"
                  >
                    <option value="">Choose a direction&hellip;</option>
                    {DIRECTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <span className="ferro-field__hint" data-testid="add-service-transport-hint">
                    This sets the transport flag on the visit. A ride in means
                    the desk confirms the dog is here before check-in.
                    {stayFlagLabel ? ` This visit is already flagged “${stayFlagLabel}”.` : ''}
                  </span>
                </label>

                {/* Ted 09-29 (lane LG adds the field): "stop offering
                    transport to new clients … keep it only for grandfathered
                    existing clients". Rendered ONLY when the server sends the
                    field — an older server omits it, and absent must never
                    read as "fine" (deploy-window render guard). A caution,
                    not a stop: staff decide, with the rule in front of them. */}
                {choice?.grandfather_caution === true ? (
                  <p
                    className="ferro-add-service__caution"
                    role="note"
                    data-testid="add-service-grandfather-caution"
                  >
                    <strong>Ted&rsquo;s rule (09-29):</strong> transport is not offered to new
                    clients — only to clients grandfathered in. This customer is not
                    marked as grandfathered. Check with a manager before adding it.
                  </p>
                ) : null}

                <label className="ferro-field ferro-field--check">
                  <input
                    type="checkbox"
                    checked={overrideOpen}
                    disabled={busy}
                    onChange={(e) => setOverrideOpen(e.target.checked)}
                    data-testid="add-service-override-toggle"
                  />
                  <span className="ferro-field__label">Charge a different price for this trip</span>
                </label>

                {overrideOpen ? (
                  <>
                    <label className="ferro-field">
                      <span className="ferro-field__label">New price</span>
                      <input
                        id="add-service-override-amount"
                        name="overrideAmount"
                        className="ferro-input"
                        type="text"
                        inputMode="decimal"
                        placeholder={money(choice?.totalCents ?? 0).slice(1)}
                        value={overrideAmount}
                        disabled={busy}
                        onChange={(e) => setOverrideAmount(e.target.value)}
                        aria-invalid={overrideAmount !== '' && overrideCents === null}
                        data-testid="add-service-override-amount"
                      />
                      <span className="ferro-field__hint">
                        Usual price {money(choice?.totalCents ?? 0)}. A change bigger than 20%
                        needs an owner or manager.
                      </span>
                    </label>
                    <label className="ferro-field">
                      <span className="ferro-field__label">Why</span>
                      <input
                        id="add-service-override-reason"
                        name="overrideReason"
                        className="ferro-input"
                        type="text"
                        maxLength={200}
                        placeholder="e.g. vet run, 40 miles"
                        value={overrideReason}
                        disabled={busy}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        data-testid="add-service-override-reason"
                      />
                      <span className="ferro-field__hint">Recorded with the charge, in the visit&rsquo;s history.</span>
                    </label>
                  </>
                ) : null}
              </>
            ) : null}

            {choice && isBatch ? (
              <p className="ferro-add-service__price" data-testid="add-service-batch-price">
                {batchDates.length === 0 ? (
                  <>Tick at least one date of the stay.</>
                ) : quoteError ? (
                  <span role="alert">{quoteError}</span>
                ) : quoting || !quote ? (
                  <>Pricing {batchDates.length} {batchDates.length === 1 ? 'day' : 'days'}&hellip;</>
                ) : newLines === 0 ? (
                  <>
                    {choice.label} is already on every date you picked — nothing to add.
                  </>
                ) : (
                  <>
                    {choice.label} — <strong>{money(quote.totalCents)}</strong> for {newLines}{' '}
                    {newLines === 1 ? 'day' : 'days'}
                    {dogCount > 1 ? `, all ${dogCount} dogs` : ''}
                    {skippedLines > 0
                      ? `; ${skippedLines} ${skippedLines === 1 ? 'date' : 'dates'} already ${skippedLines === 1 ? 'has' : 'have'} it and ${skippedLines === 1 ? 'is' : 'are'} left alone`
                      : ''}
                    . That is what this stay will be charged, priced by the server for each date; every
                    day is added to the checkout as its own line.
                  </>
                )}
              </p>
            ) : choice ? (
              <p className="ferro-add-service__price" data-testid="add-service-price">
                {isTransport && overrideOpen && overrideCents !== null ? (
                  <>
                    {choice.label} — <strong>{money(overrideCents)}</strong> instead of the
                    usual {money(choice.totalCents)}. The reason is recorded with the
                    charge; it is added to the checkout as its own line.
                  </>
                ) : (
                  <>
                    {choice.label} — <strong>{money(choice.totalCents)}</strong>
                    {dogCount > 1 ? ` for all ${dogCount} dogs on this visit` : ''}.
                    {' '}That is what this visit will be charged, priced by the
                    server for this date; it is added to the checkout as its own
                    line.
                  </>
                )}
              </p>
            ) : null}

            <p className="ferro-add-service__where">
              {parentCheckedIn
                ? 'The dog is already here, so this is added as checked in and shows on the day list and the checkout right away.'
                : 'It will show on the day list, the assigned person’s schedule, and the checkout for this visit.'}
              {isBatch
                ? ' Each date is its own line: take one off, change its price with a reason, or change who did it, without touching the others.'
                : ''}
            </p>
          </>
        )}

        {error ? (
          <p className="ferro-add-service__error" role="alert" data-testid="add-service-error">
            {error}
          </p>
        ) : null}

        <div className="ferro-add-service__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="add-service-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void submit()}
            disabled={!canSubmit}
            data-testid="add-service-submit"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
